// MUI-free copy of Ui-components/mvp-development/MvpTypes.
import React, {memo, useEffect, useRef, useState} from 'react';
import type {CSSProperties} from 'react';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import SectionHeader from '@/components/molecules/SectionHeader';
import styles from './MvpTypes.module.scss';

export interface MvpType {
    id: string;
    title: string;
    /** Callout copy under `calloutLabel`. Supply this or `points`. */
    bestSuitedFor?: string;
    /** Short capability list shown in the callout instead of `bestSuitedFor` copy. */
    points?: string[];
    /** Optional paragraph under the callout; omitted when the callout carries the detail. */
    description?: string;
    image: string | StaticImageData;
}

interface MvpTypesProps {
    heading: string;
    subheading: string;
    eyebrow: string;
    types: MvpType[];
    /** Label above the `bestSuitedFor` callout. */
    calloutLabel?: string;
    /** Desktop only: show this many tabs, then scroll the rest of the list. */
    maxVisibleTabs?: number;
    /** `center` (default): image between the tabs and a wide detail card. `right`: the
     *  detail card sits beside the tabs, and the image takes the wide right column with
     *  no caption; points render as individual pills. */
    imagePosition?: 'center' | 'right';
    /** next/image re-encode quality. Raise it when the source is already a compressed
     *  WebP, so a second lossy pass at the default 75 doesn't soften it further. */
    imageQuality?: number;
    /** Drops the navy panel: centred standard section header, white bordered cards on the page background. */
    light?: boolean;
}

// Server-side guess at one tab's height plus the list gap, used until the real tabs are
// measured (a two-line title makes a tab taller than this).
const TAB_HEIGHT_ESTIMATE = 52;
const LIST_PADDING_PX = 12;

// The frame is tall and narrow, so `cover` scales a 4:3 image by the frame's height and it
// renders far wider than its column (~510px at the 340px+ desktop height). The hint has to
// ask for that rendered width, not the column's 25vw, or smaller desktops get a file half
// the size it is drawn at, and it blurs.
const IMAGE_SIZES = '(max-width: 900px) 100vw, 560px';
const COVER = {objectFit: 'cover'} as const;

const cx = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(' ');

const renderPills = (points: string[]) => (
    <ul className={styles.pillList}>
        {points.map((point) => (
            <li key={point} className={`font-regular-text ${styles.pointItem} ${styles.pillItem}`}>
                {point}
            </li>
        ))}
    </ul>
);

const renderDetailBody = (type: MvpType, calloutLabel: string, pills = false) =>
    pills && type.points?.length ? (
        <>
            {type.description && <p className={`font-regular-text ${styles.pillDesc}`}>{type.description}</p>}
            {renderPills(type.points)}
        </>
    ) : (
        <>
            <div className={styles.callout}>
                <p className={`font-headers ${styles.calloutLabel}`}>{calloutLabel}</p>
                {type.bestSuitedFor && <p className={`font-regular-text ${styles.calloutText}`}>{parse(type.bestSuitedFor)}</p>}
                {type.points && type.points.length > 0 && (
                    <ul className={styles.pointList}>
                        {type.points.map((point) => (
                            <li key={point} className={`font-regular-text ${styles.pointItem}`}>
                                {point}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {type.description && <p className={`font-regular-text ${styles.detailDesc}`}>{type.description}</p>}
        </>
    );

const MvpTypes = ({
    heading,
    subheading,
    eyebrow,
    types,
    calloutLabel = 'Best Suited For',
    maxVisibleTabs,
    imagePosition = 'center',
    imageQuality,
    light
}: MvpTypesProps) => {
    const imageRight = imagePosition === 'right';
    const listRef = useRef<HTMLDivElement>(null);
    const scrolls = (!!maxVisibleTabs && types.length > maxVisibleTabs) || imagePosition === 'right';
    const [listMaxHeight, setListMaxHeight] = useState((maxVisibleTabs ?? 0) * TAB_HEIGHT_ESTIMATE + LIST_PADDING_PX * 2);

    // Cap the list at the bottom edge of the Nth tab, re-measured when the list resizes.
    useEffect(() => {
        const list = listRef.current;
        if (!scrolls || !list || !maxVisibleTabs) return undefined;
        const measure = () => {
            const tabs = list.querySelectorAll<HTMLElement>(':scope > button');
            const last = tabs[maxVisibleTabs - 1];
            if (last) setListMaxHeight(last.offsetTop + last.offsetHeight + LIST_PADDING_PX);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(list);
        return () => observer.disconnect();
    }, [scrolls, maxVisibleTabs]);

    const [activeId, setActiveId] = useState(types?.[0]?.id);
    const [paused, setPaused] = useState(false);
    // Starts off so the server and the first client render agree; the effect turns it on
    // after mount, and only when the visitor has not asked for reduced motion.
    const [autoplay, setAutoplay] = useState(false);
    const activeIndex = Math.max(
        0,
        types.findIndex((type) => type.id === activeId)
    );
    const activeType = types[activeIndex];

    useEffect(() => {
        if (typeof globalThis.window === 'undefined' || !globalThis.matchMedia) {
            return undefined;
        }
        const query = globalThis.matchMedia('(prefers-reduced-motion: reduce)');
        const sync = () => setAutoplay(!query.matches && types.length > 1);
        sync();
        query.addEventListener('change', sync);
        return () => query.removeEventListener('change', sync);
    }, [types.length]);

    const showNext = () => setActiveId(types[(activeIndex + 1) % types.length].id);

    // Autoplay walks past the visible tabs, so keep the active one in view. Scrolls the
    // list itself, never the page (scrollIntoView would drag the window along with it).
    useEffect(() => {
        const list = listRef.current;
        if (!scrolls || !list) return;
        const tab = list.querySelectorAll<HTMLElement>(':scope > button')[activeIndex];
        if (!tab) return;
        const top = tab.offsetTop - LIST_PADDING_PX;
        const bottom = tab.offsetTop + tab.offsetHeight + LIST_PADDING_PX;
        if (top < list.scrollTop) list.scrollTo({top, behavior: 'smooth'});
        else if (bottom > list.scrollTop + list.clientHeight) list.scrollTo({top: bottom - list.clientHeight, behavior: 'smooth'});
    }, [activeIndex, scrolls]);

    let listClass = styles.tabList;
    if (imageRight) listClass = `${styles.tabList} ${styles.rightList}`;
    else if (scrolls) listClass = `${styles.tabList} ${styles.scrollList}`;
    // The measured cap applies from md (set as a custom property, read by .scrollList).
    const listStyle = !imageRight && scrolls ? ({'--list-max-height': `${listMaxHeight}px`} as CSSProperties) : undefined;

    const fillPercent = ((activeIndex + 1) / types.length) * 100;

    return (
        <div>
            <Container>
                <div className={cx(styles.panel, light && styles.panelLight)}>
                    {light ? (
                        <SectionHeader heading={heading} subheading={parse(subheading)} />
                    ) : (
                        <>
                            <h2 className={`font-headers ${styles.heading}`}>{heading}</h2>
                            <p className={`font-regular-text ${styles.subheading}`}>{parse(subheading)}</p>
                        </>
                    )}

                    {/* Pointer or keyboard attention holds the current type in place — the
                        section should never move out from under someone reading it. */}
                    <div
                        className={styles.grid}
                        onMouseEnter={() => setPaused(true)}
                        onMouseLeave={() => setPaused(false)}
                        onFocus={() => setPaused(true)}
                        onBlur={() => setPaused(false)}
                    >
                        <div className={cx(styles.item, styles.md3, styles.itemFlex)}>
                            <div className={styles.listWrap}>
                                <div ref={listRef} className={listClass} style={listStyle}>
                                    {types.map((type, idx) => {
                                        const active = type.id === activeType?.id;
                                        return (
                                            <React.Fragment key={type.id}>
                                                <button
                                                    type="button"
                                                    onClick={() => setActiveId(type.id)}
                                                    aria-pressed={active}
                                                    className={active ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                                                >
                                                    <span className={`font-headers ${styles.tabNumber}`}>
                                                        {String(idx + 1).padStart(2, '0')}
                                                    </span>
                                                    <span className={`font-regular-text ${styles.tabLabel}`}>{type.title}</span>
                                                </button>
                                                {active && (
                                                    <div className={styles.inlineDetail}>
                                                        {renderDetailBody(type, calloutLabel, imageRight)}
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div
                            className={cx(
                                styles.item,
                                imageRight ? styles.md6 : styles.md3,
                                styles.wideOnly,
                                styles.imageCol,
                                imageRight && styles.orderImage
                            )}
                        >
                            <div className={imageRight ? `${styles.imageCard} ${styles.imageCardRight}` : styles.imageCard}>
                                {activeType && (
                                    <OptimizedImage
                                        src={activeType.image}
                                        alt={activeType.title}
                                        fill
                                        sizes={IMAGE_SIZES}
                                        quality={imageQuality}
                                        style={COVER}
                                    />
                                )}
                                {!imageRight && (
                                    <>
                                        <div className={styles.imageScrim} />
                                        <div className={styles.imageCaption}>
                                            <p className={`font-headers ${styles.imageEyebrow}`}>{eyebrow}</p>
                                            <p className={`font-headers ${styles.imageTitle}`}>{activeType?.title}</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div
                            className={cx(
                                styles.item,
                                imageRight ? styles.md3 : styles.md6,
                                styles.wideOnly,
                                styles.detailCol,
                                imageRight && styles.orderDetail
                            )}
                        >
                            {activeType && (
                                <div className={styles.detailCard}>
                                    {/* Every type's details are in the HTML (search engines read them all); only the
                                        active one shows, laid out exactly as before (`display: contents`). */}
                                    {types.map((type, idx) => (
                                        <div key={type.id} className={type.id === activeType.id ? styles.shownPanel : styles.hiddenPanel}>
                                            {!imageRight && (
                                                <p className={`font-headers ${styles.detailNumber}`}>{String(idx + 1).padStart(2, '0')}</p>
                                            )}
                                            <h3
                                                className={`font-headers ${
                                                    imageRight ? `${styles.detailTitle} ${styles.detailTitleNarrow}` : styles.detailTitle
                                                }`}
                                            >
                                                {type.title}
                                            </h3>

                                            {renderDetailBody(type, calloutLabel, imageRight)}
                                        </div>
                                    ))}

                                    <div className={styles.progressTrack}>
                                        {/* Keyed on the active type so switching restarts the
                                            fill — and therefore the dwell timer — from zero. The fill is the clock:
                                            the section advances on its `animationend`. */}
                                        <div
                                            key={activeType.id}
                                            className={cx(
                                                styles.progressFill,
                                                autoplay && styles.progressAnimated,
                                                autoplay && paused && styles.progressPaused
                                            )}
                                            style={autoplay ? undefined : {transform: `scaleX(${fillPercent / 100})`}}
                                            onAnimationEnd={autoplay ? showNext : undefined}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default memo(MvpTypes);
